import { Alert, AlertDescription, Button, Input, SimpleFormField } from '@invent/wl-ui-kit-next/ui';
import * as React from 'react';

import { ChoiceSelect } from '@/design';
import type { IEnteredNumber } from '../storage';
import type { IRegistrationView, RegistrationCaller } from '../types';
import { US_STATES } from '../us-states';
import { DOOR, unplacedFailure, validationErrors } from './registration-copy';
import { MessageList, QuietAction, StepHeading, slotMessage, useStepAction } from './step-parts';

/** The names an anchor's refusals come back under, each drawn beside its control. */
const ANCHOR_FIELDS: ReadonlySet<string> = new Set(['value', 'issuingState']);

/**
 * A person's run's `anchor` (SR-487): page 02 for the CRD, page 05 for the insurance number — by
 * the number in hand, which the view names. Back abandons the run on either: it returns to page
 * 01 with its ticks.
 *
 * **What the number leads to is survey-api's.** A match goes to "Is this you?", a number another
 * account holds ends the run (page 04), and anything else moves on with nothing said. Only FINRA's
 * first miss of a CRD stays on page 02, `lookup: 'not_found'`, for one more try — said in one line
 * above the field (Q7), with the number left in it to be checked. Each field starts with the
 * number last entered for it, so "No, that's not me" and a reload come back to it.
 *
 * Signed in (`/portal/surveys/add`) the pages are the same, and page 02 says the number is added
 * to the account rather than one created.
 */
export function NumberAnchorStep({
  view,
  caller,
  entered,
  onSubmit,
  onBack
}: {
  view: IRegistrationView;
  caller: RegistrationCaller;
  entered: IEnteredNumber | undefined;
  onSubmit: (number: IEnteredNumber) => Promise<void>;
  onBack: () => Promise<void>;
}) {
  return view.number === 'insurance' ? (
    <InsuranceAnchor entered={entered} onSubmit={onSubmit} onBack={onBack} />
  ) : (
    <CrdAnchor
      view={view}
      signedIn={caller === 'account'}
      entered={entered}
      onSubmit={onSubmit}
      onBack={onBack}
    />
  );
}

/** Page 02, "Your CRD number". */
function CrdAnchor({
  view,
  signedIn,
  entered,
  onSubmit,
  onBack
}: {
  view: IRegistrationView;
  signedIn: boolean;
  entered: IEnteredNumber | undefined;
  onSubmit: (number: IEnteredNumber) => Promise<void>;
  onBack: () => Promise<void>;
}) {
  const [value, setValue] = React.useState(entered?.value ?? '');
  const { pending, error, run } = useStepAction();
  const problems = validationErrors(error);
  const failure = unplacedFailure(error, ANCHOR_FIELDS, 'Could not check that number.');

  return (
    <>
      <StepHeading
        title={DOOR.crd.title}
        lede={signedIn ? DOOR.crd.addedLede : DOOR.crd.lede}
      />

      {view.lookup === 'not_found' && (
        <Alert className="mb-5" data-retry>
          <AlertDescription>{DOOR.crd.retry}</AlertDescription>
        </Alert>
      )}

      <form
        noValidate
        className="grid gap-5"
        onSubmit={(event) => {
          event.preventDefault();
          void run(() => onSubmit({ value, idType: 'CRD' }));
        }}
      >
        <SimpleFormField
          label={DOOR.crd.label}
          htmlFor="registration-crd"
          required
          error={slotMessage(problems.value)}
        >
          <Input
            id="registration-crd"
            value={value}
            onChange={(event) => setValue(event.target.value)}
            inputMode="numeric"
            autoComplete="off"
            aria-invalid={!!problems.value}
          />
          <MessageList messages={problems.value} />
        </SimpleFormField>

        <Failure message={failure} />
        <Actions pending={pending} onBack={() => void run(onBack)} />
      </form>
    </>
  );
}

/** The state select's choices: none yet, then the mirror's 56 by name. */
const STATE_CHOICES = [
  { value: '', label: DOOR.insurance.statePlaceholder },
  ...US_STATES.map(({ code, name }) => ({ value: code, label: name }))
];

/**
 * Page 05, "Your producer number" — an NPN, or a state licence with its state (decision 5).
 *
 * **One number is sent**: the NPN where one is typed, else the licence with its state. Both typed
 * is held here with "Give us one of them" — the one rule the page makes, since which of the two
 * to send is the registrant's to say, not the server's to guess. Nothing else is checked: an empty
 * form sends an empty NPN, a licence with no state goes without one, and survey-api's 400 lands on
 * the field it names — `value` on the number sent, `issuingState` on State.
 */
function InsuranceAnchor({
  entered,
  onSubmit,
  onBack
}: {
  entered: IEnteredNumber | undefined;
  onSubmit: (number: IEnteredNumber) => Promise<void>;
  onBack: () => Promise<void>;
}) {
  const licensed = entered?.idType === 'INSURANCE_LICENSE';
  const [npn, setNpn] = React.useState(licensed ? '' : (entered?.value ?? ''));
  const [license, setLicense] = React.useState(licensed ? entered.value : '');
  const [state, setState] = React.useState(licensed ? (entered.issuingState ?? '') : '');
  // Which field the last request sent, so its `value` refusal is drawn under it.
  const [sent, setSent] = React.useState<'NPN' | 'INSURANCE_LICENSE'>('NPN');
  const [both, setBoth] = React.useState(false);
  const { pending, error, run } = useStepAction();
  const words = DOOR.insurance;

  const problems = validationErrors(error);
  const failure = unplacedFailure(error, ANCHOR_FIELDS, 'Could not check that number.');
  const npnProblems = sent === 'NPN' ? problems.value : undefined;
  const licenseProblems = sent === 'INSURANCE_LICENSE' ? problems.value : undefined;

  return (
    <>
      <StepHeading title={words.title} lede={words.lede} />

      <form
        noValidate
        className="grid gap-5"
        onSubmit={(event) => {
          event.preventDefault();
          const npnValue = npn.trim();
          const licenseValue = license.trim();
          const twoGiven = npnValue !== '' && licenseValue !== '';
          setBoth(twoGiven);
          if (twoGiven) return;
          const number: IEnteredNumber =
            licenseValue === ''
              ? { value: npn, idType: 'NPN' }
              : {
                  value: license,
                  idType: 'INSURANCE_LICENSE',
                  ...(state ? { issuingState: state } : {})
                };
          setSent(number.idType === 'NPN' ? 'NPN' : 'INSURANCE_LICENSE');
          void run(() => onSubmit(number));
        }}
      >
        <SimpleFormField
          label={words.npn}
          htmlFor="registration-npn"
          error={slotMessage(npnProblems)}
        >
          <Input
            id="registration-npn"
            value={npn}
            onChange={(event) => setNpn(event.target.value)}
            inputMode="numeric"
            autoComplete="off"
            aria-invalid={!!npnProblems}
          />
          <MessageList messages={npnProblems} />
        </SimpleFormField>

        <div
          className="flex items-center gap-3 text-xs tracking-wide text-muted-foreground uppercase"
          aria-hidden="true"
        >
          <span className="h-px flex-1 bg-border" />
          {words.or}
          <span className="h-px flex-1 bg-border" />
        </div>

        <div className="grid gap-5 sm:grid-cols-2 sm:gap-3">
          <SimpleFormField
            label={words.license}
            htmlFor="registration-license"
            error={slotMessage(licenseProblems)}
          >
            <Input
              id="registration-license"
              value={license}
              onChange={(event) => setLicense(event.target.value)}
              placeholder={words.licensePlaceholder}
              autoComplete="off"
              aria-invalid={!!licenseProblems}
            />
            <MessageList messages={licenseProblems} />
          </SimpleFormField>

          <div className="grid content-start gap-2">
            <span id="registration-state-label" className="text-sm font-medium">
              {words.state}
            </span>
            <ChoiceSelect
              value={state}
              onChange={setState}
              choices={STATE_CHOICES}
              disabled={pending}
              labelledBy="registration-state-label"
            />
            {problems.issuingState?.map((message) => (
              <p key={message} className="text-xs text-destructive">
                {message}
              </p>
            ))}
          </div>
        </div>

        {both && (
          <p role="alert" className="text-sm font-medium text-destructive">
            {words.oneOfThem}
          </p>
        )}
        <Failure message={failure} />
        <Actions pending={pending} onBack={() => void run(onBack)} />
      </form>
    </>
  );
}

function Failure({ message }: { message: Nullable<string> }) {
  if (!message) return null;
  return (
    <p role="alert" className="text-sm font-medium text-destructive">
      {message}
    </p>
  );
}

/** Continue, and the quiet Back under it — both pages' two buttons. */
function Actions({ pending, onBack }: { pending: boolean; onBack: () => void }) {
  return (
    <div className="grid">
      <Button type="submit" disabled={pending}>
        {pending ? DOOR.checking : DOOR.continue}
      </Button>
      <QuietAction label={DOOR.back} disabled={pending} onClick={onBack} />
    </div>
  );
}
